import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { route } from 'preact-router';
import util from 'preact-util';

import { Text } from 'preact-i18n';

import { SIDEBAR_CATEGORIES, getVisibleItems, mergeWithConfig } from './sidebarDefaults';
import reloadApp from '../../lib/reloadApp';
import MenuColorLegend from '../menu/colorLegend';
import style from './style.css';

// Red = visible only because the user is a super admin (adminOnly), amber = unlocked by a role (isRoleLink).
// Same colours as the hamburger menu (components/menu/list.js).
function itemColorClass(item) {
    if (item.adminOnly) return ' text-danger';
    if (item.isRoleLink) return ' text-warning';
    return '';
}

@observer
class Sidebar extends Component {
    constructor(props) {
        super(props);
        const { appState } = props.stores;
        const { currentCustomer = {} } = appState;
        const sidebarConfig = currentCustomer?.featureFlags?.sidebarConfig;
        const defaultState = sidebarConfig?.defaultState || 'hidden';
        const savedState = localStorage.getItem('sidebarState');
        this.state = {
            sidebarState: savedState || defaultState, // 'hidden' | 'icons' | 'expanded'
            editMode: false,
            editItems: null,      // local copy during edit mode
            dragIndex: null,
            dragOverIndex: null,
        };
    }

    getVisibilityContext = () => {
        const { appState, userStore } = this.props.stores;
        const { currentCustomer = {}, opts = {}, isCordova } = appState;
        const { user = {}, isAdmin, isBlockedFromComments, isBlockedFromSupportTickets,
            hasTvRadioProducerAccess, hasRaceAdminAccess, isRaceSuperAdmin, customer = {} } = userStore;
        const { featureFlags = {} } = customer;
        return { currentCustomer, opts, user, userStore, featureFlags, isCordova, isAdmin, hasRaceAdminAccess, isRaceSuperAdmin };
    }

    getMergedItems = () => {
        const context = this.getVisibilityContext();
        const visibleItems = getVisibleItems(context);
        const { appState } = this.props.stores;
        const sidebarConfig = appState.currentCustomer?.featureFlags?.sidebarConfig;
        return mergeWithConfig(visibleItems, sidebarConfig);
    }

    setSidebarState = (newState) => {
        this.setState({ sidebarState: newState });
        localStorage.setItem('sidebarState', newState);
    }

    onClickTab = () => {
        this.setSidebarState('icons');
    }

    onExpandSidebar = () => {
        this.setSidebarState('expanded');
    }

    onCollapse = () => {
        this.setSidebarState('icons');
    }

    onClose = () => {
        this.setSidebarState('hidden');
    }

    onClickItem = (item) => {
        const { appState, saleStore } = this.props.stores;
        const { drawerHeightLarge } = appState;

        if (item.action === 'route') {
            route(item.href);
        } else if (item.action === 'drawer') {
            const method = item.drawerSide === 'right' ? 'openDrawerRight' : 'openDrawer';
            const options = { height: drawerHeightLarge, ...item.drawerOptions };
            // Use drawerLevel 2 to match header behavior
            appState[method](item.drawer, options, 2);
        } else if (item.action === 'func') {
            if (item.funcName === 'openQuickSearch') {
                const { toggleQuickSearch } = this.props;
                if (toggleQuickSearch) {
                    toggleQuickSearch();
                }
            } else if (item.funcName === 'reload') {
                reloadApp();
            } else if (item.funcName === 'createNewSale') {
                if (saleStore) {
                    saleStore.createNewSale();
                }
            } else if (item.funcName === 'showHelp') {
                appState.openDrawer('help', { height: drawerHeightLarge }, 2);
            } else if (item.funcName === 'lockScreen') {
                const { default: lockScreenStore } = require('../../stores/lockScreen');
                lockScreenStore.lock();
            } else if (item.funcName === 'runOfflinePrefetch') {
                require('../../lib/offlinePrefetch').runPrefetch();
            }
        }
    }

    isActiveRoute = (item) => {
        const { appState } = this.props.stores;
        const { path } = appState;
        if (!item.href || item.action !== 'route') {
            return false;
        }
        if (item.href === '/') {
            return path === '/' || path === '';
        }
        return path.startsWith(item.href);
    }

    getItemsWithBadges = (items) => {
        const { messageStore, supportTicketStore, formSigningRequestStore } = this.props.stores;
        const inboxUnread = messageStore?.unread || 0;
        const supportUnread = supportTicketStore?.unreadCount || 0;
        const signingUnread = formSigningRequestStore?.unread || 0;

        return items.map(item => {
            let badge = 0;
            if (item.key === 'inbox' && inboxUnread > 0) {
                badge = inboxUnread;
            }
            if ((item.key === 'support-staff' || item.key === 'support') && supportUnread > 0) {
                badge = supportUnread;
            }
            if (item.key === 'signing-requests' && signingUnread > 0) {
                badge = signingUnread;
            }
            return badge > 0 ? { ...item, badge } : item;
        });
    }

    toggleEditMode = () => {
        const { editMode } = this.state;
        if (!editMode) {
            const mergedItems = this.getMergedItems();
            const items = this.getItemsWithBadges(mergedItems);
            this.setState({ editMode: true, editItems: items });
        } else {
            this.saveConfig();
            this.setState({ editMode: false, editItems: null });
        }
    }

    saveConfig = async () => {
        const { editItems } = this.state;
        const { appState, customerStore } = this.props.stores;
        const { currentCustomer } = appState;
        if (!editItems || !currentCustomer?.id) return;

        const existingConfig = currentCustomer?.featureFlags?.sidebarConfig || {};
        const configItems = editItems.map((item, idx) => {
            if (item.isCustom) {
                return {
                    type: 'custom',
                    icon: item.icon,
                    text: item.label,
                    href: item.href,
                    visible: item.visible !== false,
                    order: idx,
                };
            }
            return {
                type: 'default',
                key: item.key,
                visible: item.visible !== false,
                order: idx,
            };
        });

        const newConfig = { ...existingConfig, items: configItems };

        // Pass object directly — backend jsonFeatureFlags handler passes it through to MongoDB
        await customerStore.saveField(currentCustomer.id, 'featureFlags.sidebarConfig', newConfig);

        // Update appState immediately for live preview
        const updatedCustomer = {
            ...currentCustomer,
            featureFlags: {
                ...currentCustomer.featureFlags,
                sidebarConfig: newConfig,
            },
        };
        appState.updateField('currentCustomer', updatedCustomer);
    }

    handleDragStart = (e) => {
        const index = parseInt(e.target.closest('[data-index]').dataset.index, 10);
        this.setState({ dragIndex: index });
        e.dataTransfer.effectAllowed = 'move';
    }

    handleDragOver = (e) => {
        e.preventDefault();
        const target = e.target.closest('[data-index]');
        if (!target) return;
        const overIndex = parseInt(target.dataset.index, 10);
        this.setState({ dragOverIndex: overIndex });
    }

    handleDrop = (e) => {
        e.preventDefault();
        const { dragIndex, editItems } = this.state;
        const target = e.target.closest('[data-index]');
        if (!target) return;
        const dropIndex = parseInt(target.dataset.index, 10);

        if (dragIndex === dropIndex) {
            this.setState({ dragIndex: null, dragOverIndex: null });
            return;
        }

        const newItems = [...editItems];
        const [draggedItem] = newItems.splice(dragIndex, 1);
        newItems.splice(dropIndex, 0, draggedItem);
        this.setState({ editItems: newItems, dragIndex: null, dragOverIndex: null });
    }

    handleDragEnd = () => {
        this.setState({ dragIndex: null, dragOverIndex: null });
    }

    toggleItemVisibility = (index) => {
        const { editItems } = this.state;
        const newItems = [...editItems];
        newItems[index] = { ...newItems[index], visible: !newItems[index].visible };
        this.setState({ editItems: newItems });
    }

    addCustomItem = () => {
        const { editItems } = this.state;
        const newItem = {
            key: `custom-${Date.now()}`,
            category: 'custom',
            icon: 'fa-solid fa-link',
            label: 'New Link',
            action: 'route',
            href: '/',
            visible: true,
            isCustom: true,
            isEditing: true,
        };
        this.setState({ editItems: [...editItems, newItem] });
    }

    updateCustomItem = (index, field, value) => {
        const { editItems } = this.state;
        const newItems = [...editItems];
        newItems[index] = { ...newItems[index], [field]: value };
        this.setState({ editItems: newItems });
    }

    deleteCustomItem = (index) => {
        const { editItems } = this.state;
        const newItems = editItems.filter((_, i) => i !== index);
        this.setState({ editItems: newItems });
    }

    renderIconsState = (items, brandColor, isAdminUser) => {
        return (<>
            <div class='d-flex justify-content-between'>
                <div class={style.sidebarCloseBtn} onClick={this.onClose} title='Hide menu'>
                    <i class='fa-solid fa-chevron-left' />
                </div>
                <div class={style.sidebarToggleBtn} onClick={this.onExpandSidebar} title='Expand menu'>
                    <i class='fa-solid fa-chevron-right' />
                </div>
            </div>
            <div class={style.sidebarIconList}>
                {(() => {
                    let lastCat = null;
                    return items.map(item => {
                        const showDiv = item.category !== lastCat && lastCat !== null;
                        lastCat = item.category;
                        return (<>
                            {showDiv && <div class={style.sidebarIconDivider} />}
                            <div
                                class={`${style.sidebarIconItem} position-relative`}
                                style={this.isActiveRoute(item) ? `background-color: ${brandColor}15; color: ${brandColor};` : ''}
                                title={item.label}
                                onClick={(e) => { e.stopPropagation(); this.onClickItem(item); }}
                            >
                                <i class={`${item.icon}${itemColorClass(item)}`} />
                                {item.badge > 0 && (
                                    <span class='position-absolute top-0 end-0 badge rounded-pill bg-danger' style='font-size: 8px; padding: 2px 4px;'>
                                        {item.badge}
                                    </span>
                                )}
                            </div>
                        </>);
                    });
                })()}
            </div>
            {isAdminUser && (
                <div class={style.sidebarFooter}>
                    <div
                        class={style.sidebarIconItem}
                        title='Configure menu'
                        onClick={() => { this.onExpandSidebar(); setTimeout(() => this.toggleEditMode(), 250); }}
                    >
                        <i class='fa-solid fa-gear' />
                    </div>
                </div>
            )}
        </>);
    }

    renderExpandedState = (items, brandColor, isAdminUser, editMode) => {
        const { dragIndex, dragOverIndex } = this.state;
        let lastCategory = null;

        return (<>
            <div class={style.sidebarCollapseHeader} onClick={this.onCollapse} title='Collapse menu'>
                <i class='fa-solid fa-chevron-left' style='margin-right: 8px;' />
                <span><Text id='sidebar.collapse'>Collapse</Text></span>
            </div>
            <div class={style.sidebarItemList}>
                {items.map((item, index) => {
                    if (editMode) {
                        const isBeingDragged = dragIndex === index;
                        const isDragOver = dragOverIndex === index && dragIndex !== index;
                        const isHidden = item.visible === false;

                        return (
                            <div
                                class={`${style.sidebarItem} ${isHidden ? style.sidebarItemHidden : ''}`}
                                style={isDragOver ? 'border-top: 2px solid var(--bs-primary);' : isBeingDragged ? 'opacity: 0.4;' : ''}
                                draggable='true'
                                data-index={index}
                                onDragStart={this.handleDragStart}
                                onDragOver={this.handleDragOver}
                                onDrop={this.handleDrop}
                                onDragEnd={this.handleDragEnd}
                            >
                                <i class={`fa-solid fa-grip-vertical ${style.sidebarEditDragHandle}`} />
                                <i class={`${style.sidebarItemIcon} ${item.icon}`} />
                                {item.isCustom && item.isEditing ? (
                                    <div style='display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0;'>
                                        <input
                                            class='form-control form-control-sm'
                                            placeholder='Label'
                                            value={item.label}
                                            onInput={(e) => this.updateCustomItem(index, 'label', e.target.value)}
                                        />
                                        <input
                                            class='form-control form-control-sm'
                                            placeholder='Icon class (e.g. fa-solid fa-link)'
                                            value={item.icon}
                                            onInput={(e) => this.updateCustomItem(index, 'icon', e.target.value)}
                                        />
                                        <input
                                            class='form-control form-control-sm'
                                            placeholder='URL path (e.g. /calendar)'
                                            value={item.href}
                                            onInput={(e) => this.updateCustomItem(index, 'href', e.target.value)}
                                        />
                                    </div>
                                ) : (
                                    <span style='flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis;'>
                                        <Text id={`sidebar.${item.key}`}>{item.label}</Text>
                                    </span>
                                )}
                                {item.isCustom && (
                                    <i
                                        class='fa-solid fa-trash text-danger'
                                        style='cursor: pointer; opacity: 0.5; margin-left: 4px;'
                                        onClick={() => this.deleteCustomItem(index)}
                                    />
                                )}
                                {isHidden ? (
                                    <i
                                        class={`fa-solid fa-eye-slash ${style.sidebarEditToggle} ${style.sidebarEditToggleHidden}`}
                                        onClick={() => this.toggleItemVisibility(index)}
                                    />
                                ) : (
                                    <i
                                        class={`fa-solid fa-eye ${style.sidebarEditToggle}`}
                                        onClick={() => this.toggleItemVisibility(index)}
                                    />
                                )}
                            </div>
                        );
                    }

                    const showDivider = item.category !== lastCategory && lastCategory !== null;
                    const category = SIDEBAR_CATEGORIES.find(c => c.key === item.category);
                    const showLabel = item.category !== lastCategory && category && category.label;
                    lastCategory = item.category;

                    return (<>
                        {showDivider && <div class={style.sidebarDivider} />}
                        {showLabel && (
                            <div class={`${style.sidebarCategoryLabel} text-body-secondary`}>
                                {category.label}
                            </div>
                        )}
                        <div
                            class={style.sidebarItem}
                            style={this.isActiveRoute(item) ? `background-color: ${brandColor}15; color: ${brandColor};` : ''}
                            onClick={() => this.onClickItem(item)}
                        >
                            <i class={`${style.sidebarItemIcon} ${item.icon}${itemColorClass(item)}`} />
                            <span class={item.adminOnly || item.isRoleLink ? `${itemColorClass(item)} font-weight-lighter font-style-narrow` : ''}><Text id={`sidebar.${item.key}`}>{item.label}</Text></span>
                            {item.badge > 0 && (
                                <span class={`${style.sidebarBadge} bg-danger text-white`}>{item.badge}</span>
                            )}
                        </div>
                    </>);
                })}
            </div>
            <div class={style.sidebarFooter}>
                {isAdminUser && editMode && (<>
                    <div class={style.sidebarItem} onClick={this.addCustomItem}>
                        <i class={`${style.sidebarItemIcon} fa-solid fa-plus`} />
                        <span><Text id='sidebar.add-custom-link'>Add custom link</Text></span>
                    </div>
                    <div class={style.sidebarItem} onClick={this.toggleEditMode}>
                        <i class={`${style.sidebarItemIcon} fa-solid fa-check`} />
                        <span><Text id='sidebar.done'>Done</Text></span>
                    </div>
                </>)}
                {isAdminUser && !editMode && (
                    <div class={style.sidebarItem} style='opacity: 0.6;' onClick={this.toggleEditMode}>
                        <i class={`${style.sidebarItemIcon} fa-solid fa-gear`} />
                        <span><Text id='sidebar.configure-menu'>Configure menu</Text></span>
                    </div>
                )}
                {!editMode && <MenuColorLegend class='mt-2' />}
            </div>
        </>);
    }

    render() {
        const { sidebarState, editMode, editItems } = this.state;
        const { appState, userStore } = this.props.stores;
        const { currentCustomer = {} } = appState;
        const brandColor = currentCustomer.color || '#e91e8c';
        const navbarHeight = currentCustomer?.logoHeight || '45px';
        const isAdminUser = userStore.isAdmin || userStore.isCustomerAdmin;

        const mergedItems = editMode && editItems ? editItems : this.getMergedItems();
        const items = editMode && editItems ? mergedItems : this.getItemsWithBadges(mergedItems);

        // State CSS classes
        const stateClass = sidebarState === 'hidden' ? style.sidebarHidden
            : sidebarState === 'icons' ? style.sidebarIcons
            : style.sidebarExpanded;
        const spacerClass = sidebarState === 'hidden' ? style.sidebarSpacerHidden
            : sidebarState === 'icons' ? style.sidebarSpacerIcons
            : style.sidebarSpacerExpanded;

        return (<>
            {/* Spacer in document flow */}
            <div class={`${style.sidebarSpacer} ${spacerClass} d-none d-md-block`} />

            {/* Tab when hidden */}
            {sidebarState === 'hidden' && (
                <div
                    class={`${style.sidebarTab} d-none d-md-flex`}
                    style={`background-color: ${brandColor}; top: calc(50vh);`}
                    onClick={this.onClickTab}
                >
                    <i class='fa-solid fa-chevron-right' style='color: white; font-size: 10px;' />
                </div>
            )}

            {/* Fixed sidebar */}
            <div
                class={`${style.sidebar} ${stateClass} d-none d-md-flex`}
                role='navigation'
                aria-label='Sidebar menu'
                aria-expanded={sidebarState !== 'hidden'}
                style={`top: ${navbarHeight}; background-color: var(--bs-body-bg); border-right: 1px solid var(--bs-border-color);`}
            >
                {sidebarState === 'icons' && this.renderIconsState(items, brandColor, isAdminUser)}
                {sidebarState === 'expanded' && this.renderExpandedState(items, brandColor, isAdminUser, editMode)}
            </div>
        </>);
    }
}

export default Sidebar;
